/**
 * Overlays: records that open over the whole conversation instead of beside it (a contract review, a project,
 * a deliverable, a task). Each AgentSurface owns one slot and provides it to everything it renders. Closing
 * returns to the conversation exactly as it was: nothing navigates.
 */
import { computed, inject, markRaw, reactive, type Component, type InjectionKey } from 'vue'
import { artifactKey, type ArtifactRef, type ArtifactType } from './artifacts'

export interface AgentOverlays {
    readonly current: ArtifactRef | null
    readonly isOpen: boolean
    open(a: ArtifactRef): void
    close(): void
}

export const OVERLAYS_KEY: InjectionKey<AgentOverlays> = Symbol('agentOverlays')

export function createOverlays(): AgentOverlays {
    const st = reactive({ current: null as ArtifactRef | null })
    const isOpen = computed(() => st.current !== null)
    return {
        get current() { return st.current },
        get isOpen() { return isOpen.value },
        open(a) { st.current = a },
        close() { st.current = null },
    }
}

export function useOverlays(): AgentOverlays | null {
    return inject(OVERLAYS_KEY, null)
}

/** `fullscreen`: a card inset from the window edges with a dimmed backdrop. `popup`: the component draws its own popup. */
export type OverlayLayout = 'fullscreen' | 'popup'

export const OVERLAY_LAYOUT: Partial<Record<ArtifactType, OverlayLayout>> = {
    agreement: 'fullscreen', project: 'fullscreen', deliverable: 'fullscreen', task: 'popup',
}

const registry = new Map<ArtifactType, Component>()
/** Which component shows each overlay type (registered by ArtifactOverlayHost; previews and tests can swap one). */
export function registerArtifactOverlay(type: ArtifactType, c: Component): void {
    registry.set(type, markRaw(c))
}
/** A default that never replaces an explicit registration (previews and tests register theirs first). */
export function registerDefaultArtifactOverlay(type: ArtifactType, c: Component): void {
    if (!registry.has(type)) registry.set(type, markRaw(c))
}
export function overlayFor(type: ArtifactType): Component | null {
    return registry.get(type) ?? null
}
export function overlayKey(a: ArtifactRef): string {
    return artifactKey(a)
}
